import React, { useMemo, useState, useRef, useEffect } from 'react';
import Select from 'react-select';
import Checkbox from './Checkbox';
import { useTranslation } from '../../hooks/useTranslation';

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    border: '1px solid #E6E6E6',
    borderRadius: '8px',
    py:3,
    minHeight: '48px',
    paddingRight: '5px',
    paddingLeft: '5px',
    background: '#F5F5F5',
    boxShadow: state.isFocused ? '0 0 0 2px #5D4A9D' : 'none',
    '&:hover': { border: '1px solid #E6E6E6' }
  }),
  placeholder: (provided) => ({ ...provided, color: '#9CA3AF' }),
  singleValue: (provided) => ({ ...provided, color: '#111827' }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '8px',
    padding: '8px 12px',
    border: '1px solid #E0DEF7',
    boxShadow: '0px 10px 10px 0px #100A551A',
    background: '#ffffff',
    zIndex: 50
  }),
  option: (provided, state) => ({
    ...provided,
    borderRadius: "8px",
    backgroundColor: state.isSelected ? '#5D4A9D1A' : state.isFocused ? '#5D4A9D1A' : 'white',
    color: state.isSelected ? '#5D4A9D' : '#111827',
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#E0DEF7',
    borderRadius: '4px',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#5D4A9D',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#5D4A9D',
    ':hover': {
      backgroundColor: '#5D4A9D',
      color: 'white',
    },
  }),
};

// Helpers to build translated options based on provided taxonomy
const getCategoryOptions = (t) => ([
  { value: 'performingArts', label: t('registration.talent.categoryOptions.performingArts') },
  { value: 'visualFineArts', label: t('registration.talent.categoryOptions.visualFineArts') },
  { value: 'creativeDigital', label: t('registration.talent.categoryOptions.creativeDigital') },
  { value: 'culturalHandicrafts', label: t('registration.talent.categoryOptions.culturalHandicrafts') },
  { value: 'interactiveSports', label: t('registration.talent.categoryOptions.interactiveSports') },
]);

const getSubCategoryMap = (t) => ({
  performingArts: [
    { value: 'singingMusicPerformance', label: t('registration.talent.subCategoryOptions.performing.singingMusicPerformance') },
    { value: 'instrumentalPerformance', label: t('registration.talent.subCategoryOptions.performing.instrumentalPerformance') },
    { value: 'actingTheatre', label: t('registration.talent.subCategoryOptions.performing.actingTheatre') },
    { value: 'danceMovementShows', label: t('registration.talent.subCategoryOptions.performing.danceMovementShows') },
    { value: 'poetrySpokenWord', label: t('registration.talent.subCategoryOptions.performing.poetrySpokenWord') },
  ],
  visualFineArts: [
    { value: 'paintingSculpture', label: t('registration.talent.subCategoryOptions.visualFineArts.paintingSculpture') },
    { value: 'photography', label: t('registration.talent.subCategoryOptions.visualFineArts.photography') },
    { value: 'digitalArtGraphicDesign', label: t('registration.talent.subCategoryOptions.visualFineArts.digitalArtGraphicDesign') },
    { value: 'liveArtDemo', label: t('registration.talent.subCategoryOptions.visualFineArts.liveArtDemo') },
  ],
  creativeDigital: [
    { value: 'contentCreationVlogging', label: t('registration.talent.subCategoryOptions.creativeDigital.contentCreationVlogging') },
    { value: 'videoEditingDirecting', label: t('registration.talent.subCategoryOptions.creativeDigital.videoEditingDirecting') },
    { value: 'arVrExperiences', label: t('registration.talent.subCategoryOptions.creativeDigital.arVrExperiences') },
    { value: 'liveArtDemo', label: t('registration.talent.subCategoryOptions.creativeDigital.liveArtDemo') },
    { value: 'creativeCodingTechArt', label: t('registration.talent.subCategoryOptions.creativeDigital.creativeCodingTechArt') },
  ],
  culturalHandicrafts: [
    { value: 'handmadeCrafts', label: t('registration.talent.subCategoryOptions.culturalHandicrafts.handmadeCrafts') },
    { value: 'embroideryTailoring', label: t('registration.talent.subCategoryOptions.culturalHandicrafts.embroideryTailoring') },
    { value: 'traditionalPerformingArts', label: t('registration.talent.subCategoryOptions.culturalHandicrafts.traditionalPerformingArts') },
    { value: 'liveArtDemo', label: t('registration.talent.subCategoryOptions.culturalHandicrafts.liveArtDemo') },
    { value: 'creativeCodingTechArt', label: t('registration.talent.subCategoryOptions.culturalHandicrafts.creativeCodingTechArt') },
    { value: 'cooking', label: t('registration.talent.subCategoryOptions.culturalHandicrafts.cooking') },
  ],
  interactiveSports: [
    { value: 'sportsPerformances', label: t('registration.talent.subCategoryOptions.interactiveSports.sportsPerformances') },
    { value: 'magicAcrobatics', label: t('registration.talent.subCategoryOptions.interactiveSports.magicAcrobatics') },
    { value: 'eventHostingMc', label: t('registration.talent.subCategoryOptions.interactiveSports.eventHostingMc') },
  ],
});

// const areaMap = {
//   performingArts: {
//     singingMusicPerformance: ['cornicheNorth', 'baytWoog', 'waterfrontSouth'],
//     instrumentalPerformance: ['cornicheNorth', 'baytWoog', 'waterfrontSouth'],
//     danceMovementShows: ['cornicheNorth', 'baytWoog', 'waterfrontSouth'],
//     actingTheatre: ['ithra', 'sabic', 'baytWoog'],
//     poetrySpokenWord: ['ithra', 'sabic', 'baytWoog'],
//   },
//   visualFineArts: {
//     paintingSculpture: ['ithra', 'corniche', 'sabic'],
//     photography: ['ithra', 'corniche', 'sabic'],
//     digitalArtGraphicDesign: ['ithra', 'corniche', 'sabic'],
//     liveArtDemo: ['ithra', 'corniche', 'sabic'],
//   },
//   creativeDigital: {
//     contentCreationVlogging: ['lussoAirpark', 'ithra'],
//     videoEditingDirecting: ['lussoAirpark', 'ithra'],
//     arVrExperiences: ['lussoAirpark', 'ithra'],
//     liveArtDemo: ['lussoAirpark', 'ithra'],
//     creativeCodingTechArt: ['lussoAirpark', 'ithra'],
//   },
//   culturalHandicrafts: {
//     handmadeCrafts: ['waterfront', 'sandBeach'],
//     embroideryTailoring: ['waterfront', 'sandBeach'],
//     traditionalPerformingArts: ['waterfront', 'sandBeach'],
//     liveArtDemo: ['waterfront', 'sandBeach'],
//     creativeCodingTechArt: ['waterfront', 'sandBeach'],
//   },
//   interactiveSports: {
//     sportsPerformances: ['sandBeach', 'waterfrontSouth'],
//     magicAcrobatics: ['sandBeach', 'waterfrontSouth'],
//     eventHostingMc: ['allAreas'],
//   },
// };

export default function Step2({ formData, setFormData }) {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});

  const categoryOptions = useMemo(() => getCategoryOptions(t), [t]);
  const subCategoryMap = useMemo(() => getSubCategoryMap(t), [t]);
  
  // Refs to hold latest values to avoid stale closures in onBlur on mobile
  const categoryRef = useRef(formData.category || []);
  const subCategoryRef = useRef(formData.subCategory || []);

  useEffect(() => {
    categoryRef.current = formData.category || [];
    subCategoryRef.current = formData.subCategory || [];
  }, [formData.category, formData.subCategory]);

  const currentSubCats = useMemo(() => {
    if (!formData.category || !Array.isArray(formData.category)) return [];
    return formData.category.flatMap(cat => subCategoryMap[cat.value] || []);
  }, [formData.category, subCategoryMap]);

  const currentAreas = ['EasternProvince', 'WesternProvince', 'Riyadh', 'other'];
  
  const toggleArea = (value) => {
    const list = new Set(formData.preferredAreas || []);
    if (list.has(value)) list.delete(value); else list.add(value);
    const next = Array.from(list);
    setFormData((prev) => ({ ...prev, preferredAreas: next }));
    setErrors((prev) => ({
      ...prev,
      preferredAreas: next.length > 0 ? '' : t('validation.required'),
      otherLocation: next.includes('other') && !(String(formData.otherLocation || '').trim()) ? t('validation.required') : ''
    }));
  };

  const onCategoryChange = (v) => {
    const val = v || [];
    categoryRef.current = val;
    subCategoryRef.current = []; // Clear subCategory ref as well
    setFormData((prev) => ({ ...prev, category: val, subCategory: [] }));
    setErrors((prev) => ({ ...prev, category: val.length > 0 ? '' : t('validation.required'), subCategory: t('validation.required') }));
  };

  const onSubCategoryChange = (v) => {
    const val = v || [];
    subCategoryRef.current = val;
    setFormData((prev) => ({ ...prev, subCategory: val }));
    setErrors((prev) => ({ ...prev, subCategory: val.length > 0 ? '' : t('validation.required') }));
  };

  return (
    <div className="space-y-5">
      <p className='text-[20px]  my-5 font-[700] text-[#5D4A9D] text-start'>{t('registration.step2Title')}</p>

      <div className=" gap-4">
        <div className="mb-4 text-start">
          <label className="text-[14px]   font-[500] mb-2">{t('registration.talent.category.label')} <span>*</span></label>
          <Select
            options={categoryOptions}
            value={formData.category || []}
            onChange={onCategoryChange}
            onBlur={() => {
              const val = categoryRef.current;
              setErrors((prev) => ({ ...prev, category: val && val.length > 0 ? '' : t('validation.required') }));
            }}
            placeholder={t('registration.talent.category.placeholder')}
            styles={customStyles}
            isMulti
          />
          {errors.category ? <p className="text-red-500 text-[12px] mt-1 text-start ">{errors.category}</p> : null}
        </div>
        <div className="mb-4 text-start">
          <label className="text-[14px]   font-[500] mb-2">{t('registration.talent.subCategory.label')} <span>*</span></label>
          <Select
            options={currentSubCats}
            value={formData.subCategory || []}
            onChange={onSubCategoryChange}
            onBlur={() => {
              const val = subCategoryRef.current;
              setErrors((prev) => ({ ...prev, subCategory: val && val.length > 0 ? '' : t('validation.required') }));
            }}
            placeholder={t('registration.talent.subCategory.placeholder')}
            styles={customStyles}
            isMulti
          />
          {errors.subCategory ? <p className="text-red-500 text-[12px] mt-1 text-start ">{errors.subCategory}</p> : null}
        </div>
      </div>

      {currentAreas.length > 0 && (
        <div className="text-start pb-4">
          <label className="text-[16px]  font-[500] mb-2">{t('registration.talent.preferredAreas.label')} <span>*</span></label>
          <div className="mt-2 space-y-4">
            {currentAreas.map((value) => (
              <Checkbox
                key={value}
                label={t(`registration.talent.preferredAreas.options.${value}`)}
                checked={(formData.preferredAreas || []).includes(value)}
                onChange={() => toggleArea(value)}
              />
            ))}
            {Array.isArray(formData.preferredAreas) && formData.preferredAreas.includes('other') && (
              <div className="mt-2">
                <label className="block primary-text text-[14px] font-[500] mb-1">{t('registration.talent.preferredAreas.otherLocation.label')} *</label>
                <input
                  type="text"
                  value={formData.otherLocation || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData((prev) => ({ ...prev, otherLocation: val }));
                    setErrors((prev) => ({
                      ...prev,
                      otherLocation: (String(val || '').trim()) ? '' : t('validation.required')
                    }));
                  }}
                  placeholder={t('registration.talent.preferredAreas.otherLocation.placeholder')}
                  className="w-full border-[1px] rounded-[8px] px-[18px] py-2 bg-[#F5F5F5] border-[#E6E6E6] h-[46px] placeholder:text-[16px] text-[16px] focus:bg-[#fff] focus:outline-none focus:ring-[1.5px] focus:ring-[#5D4A9D]"
                />
                {errors.otherLocation ? <p className="text-red-500 text-[12px] mt-1 text-start ">{errors.otherLocation}</p> : null}
              </div>
            )}
          </div>
          {errors.preferredAreas ? <p className="text-red-500 text-[12px] mt-1 text-start ">{errors.preferredAreas}</p> : null}
        </div>
      )}
    </div>
  );
}